// src/pages/sqf/SqfPoliciesPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import sqfAiService from '../../services/sqfAiService';
import { useLanguage, t } from '../../context/LanguageContext';
import PolicyDocumentView from '../../components/PolicyDocumentView';
import SqfNav from '../../components/SqfNav';

const CATEGORY_LABELS = {
  food_safety_plan:    'Food Safety Plan',
  ssop:               'SSOP',
  sop:                'SOP',
  prerequisite_program: 'Prerequisite Program',
  haccp:              'HACCP',
  monitoring:         'Monitoring',
  corrective_action:  'Corrective Action',
  verification:       'Verification',
  training:           'Training',
  recall:             'Recall',
  pest_control:       'Pest Control',
  allergen:           'Allergen',
  crisis_management:  'Crisis Management',
  supplier:           'Supplier',
  contractor:         'Contractor',
  visitor:            'Visitor',
  vendor:             'Vendor',
  other:              'Other',
};

const CATEGORY_COLORS = {
  food_safety_plan:    'danger',
  ssop:               'primary',
  sop:                'info',
  prerequisite_program: 'warning',
  haccp:              'danger',
  monitoring:         'success',
  corrective_action:  'warning',
  verification:       'info',
  training:           'primary',
  recall:             'danger',
  pest_control:       'success',
  allergen:           'warning',
  crisis_management:  'danger',
  supplier:           'secondary',
  contractor:         'primary',
  visitor:            'info',
  vendor:             'warning',
  other:              'secondary',
};


export default function SqfPoliciesPage() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New policy modal
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('other');
  const [newDesc, setNewDesc] = useState('');
  const [newChapterRef, setNewChapterRef] = useState('');
  const [newDocLang, setNewDocLang] = useState('en');
  const [creating, setCreating] = useState(false);

  // Generate with AI modal
  const [showGenModal, setShowGenModal] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [genCategory, setGenCategory] = useState('other');
  const [genChapterRef, setGenChapterRef] = useState('');
  const [genLanguage, setGenLanguage] = useState('en');
  const [generating, setGenerating] = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState('');

  const [viewPolicy, setViewPolicy] = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const [viewLang, setViewLang] = useState('en');

  // Delete (permanent) state
  const [confirmDeletePolicy, setConfirmDeletePolicy] = useState(null); // policy object to confirm
  const [deletingId, setDeletingId] = useState(null);

  const openView = async (policy) => {
    setLoadingView(true);
    setViewLang(lang === 'es' ? 'es' : 'en');
    try {
      const full = await sqfService.getPolicy(policy.id);
      const version = (full.Versions || []).find(v => v.id === full.activeVersionId);
      setViewPolicy({ policy: full, version });
    } catch {
      setError(t('Failed to load policy content.', 'Error al cargar el contenido de la política.', lang));
    } finally {
      setLoadingView(false);
    }
  };

  useEffect(() => {
    sqfService.getPolicies()
      .then(setPolicies)
      .catch(() => setError(t('Failed to load policies.', 'Error al cargar las políticas.', lang)))
      .finally(() => setLoading(false));
  }, [lang]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const policy = await sqfService.createPolicy({
        name: newName,
        category: newCategory,
        description: newDesc || null,
        sqfChapterRef: newChapterRef || null,
        documentLanguage: newDocLang,
      });
      setShowModal(false);
      setNewName(''); setNewCategory('other'); setNewDesc(''); setNewChapterRef(''); setNewDocLang('en');
      navigate(`/sqf/policies/${policy.id}/edit`);
    } catch {
      setError(t('Failed to create policy.', 'Error al crear la política.', lang));
    } finally {
      setCreating(false);
    }
  };

  const handleGenerate = async () => {
    if (!genPrompt.trim()) return;
    setGenerating(true);
    setError('');
    try {
      const result = await sqfAiService.generatePolicy({
        prompt: genPrompt,
        sqfChapterRef: genChapterRef || null,
        category: genCategory,
        language: genLanguage,
      });
      setShowGenModal(false);
      setGenPrompt(''); setGenCategory('other'); setGenChapterRef(''); setGenLanguage('en');
      navigate(`/sqf/policies/${result.policyId}/edit`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('Failed to generate policy.', 'Error al generar la política.', lang));
    } finally {
      setGenerating(false);
    }
  };

  const handleDeletePermanent = async () => {
    if (!confirmDeletePolicy) return;
    const policy = confirmDeletePolicy;
    setConfirmDeletePolicy(null);
    setDeletingId(policy.id);
    try {
      await sqfService.deletePolicyPermanent(policy.id);
      setPolicies(prev => prev.filter(p => p.id !== policy.id));
    } catch (err) {
      setError(err.response?.data?.message || t('Failed to delete policy.', 'Error al eliminar la política.', lang));
    } finally {
      setDeletingId(null);
    }
  };

  const active = policies.filter(p => !p.archivedAt && (!filterCategory || p.category === filterCategory));
  const archived = policies.filter(p => p.archivedAt);

  // Only show categories that have at least one policy for the filter buttons
  const usedCategories = [...new Set(policies.filter(p => !p.archivedAt).map(p => p.category))];

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h4 className="mb-0">{t('Policies & Documents', 'Políticas y Documentos', lang)}</h4>
        <div className="d-flex gap-2 flex-wrap">
          <Link to="/sqf/ai-import" className="btn btn-outline-primary btn-sm">✨ {t('Import with AI', 'Importar con IA', lang)}</Link>
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowGenModal(true)}>🤖 {t('Generate with AI', 'Generar con IA', lang)}</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ {t('New Policy', 'Nueva Política', lang)}</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}<button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}

      {/* Category filter */}
      {usedCategories.length > 1 && (
        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
          {usedCategories.map(k => (
            <button key={k}
              className={`btn btn-sm ${filterCategory === k ? `btn-${CATEGORY_COLORS[k] || 'secondary'}` : 'btn-outline-secondary'}`}
              onClick={() => setFilterCategory(filterCategory === k ? '' : k)}>
              {CATEGORY_LABELS[k] || k}
            </button>
          ))}
          {filterCategory && (
            <button className="btn btn-sm btn-link text-muted p-0 ms-1" onClick={() => setFilterCategory('')}>
              {t('Clear', 'Limpiar', lang)}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
      ) : active.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: 40 }}>📄</div>
          <p className="mt-2">{t('No policies yet.', 'Aún no hay políticas.', lang)}</p>
          <div className="d-flex gap-2 justify-content-center">
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ {t('New Policy', 'Nueva Política', lang)}</button>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowGenModal(true)}>🤖 {t('Generate with AI', 'Generar con IA', lang)}</button>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>{t('Name', 'Nombre', lang)}</th>
                <th>{t('Category', 'Categoría', lang)}</th>
                <th style={{ width: 90 }}>{t('SQF Ref', 'Ref SQF', lang)}</th>
                <th style={{ width: 70 }}>{t('Version', 'Versión', lang)}</th>
                <th style={{ width: 110 }}>{t('Status', 'Estado', lang)}</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {active.map(p => {
                const published = (p.Versions || []).find(v => v.status === 'published');
                const draft = (p.Versions || []).find(v => v.status === 'draft');
                return (
                  <tr key={p.id}>
                    <td className="fw-semibold" style={{ fontSize: 13 }}>
                      <div className="d-flex align-items-center gap-1 flex-wrap">
                        <span>{(lang === 'es' && p.nameEs) ? p.nameEs : p.name}</span>
                        {p.documentLanguage === 'both' && (
                          <span style={{ fontSize: 13, lineHeight: 1 }} title="Bilingual">🇺🇸🇪🇸</span>
                        )}
                        {p.documentLanguage === 'es' && (
                          <span style={{ fontSize: 13, lineHeight: 1 }} title="Spanish">🇪🇸</span>
                        )}
                        {p.aiGenerated && <span className="badge bg-light text-dark border" style={{ fontSize: 10 }} title={t('Generated by AI', 'Generado por IA', lang)}>🤖</span>}
                      </div>
                      {p.description && <div className="text-muted fw-normal" style={{ fontSize: 11 }}>{p.description}</div>}
                    </td>
                    <td>
                      <span className={`badge bg-${CATEGORY_COLORS[p.category] || 'secondary'} ${['other','vendor','supplier','secondary'].includes(p.category) ? 'text-dark border' : ''}`} style={{ fontSize: 10 }}>
                        {CATEGORY_LABELS[p.category] || p.category}
                      </span>
                    </td>
                    <td style={{ fontSize: 11, color: '#666' }}>
                      {p.sqfChapterRef || <span className="text-muted">—</span>}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {published ? `v${published.versionNumber}` : '—'}
                      {draft && <span className="badge bg-secondary ms-1" style={{ fontSize: 9 }}>{t('draft', 'borrador', lang)}</span>}
                    </td>
                    <td>
                      {p.activeVersionId
                        ? <span className="badge bg-success">{t('Published', 'Publicado', lang)}</span>
                        : <span className="badge bg-secondary">{t('No published version', 'Sin versión publicada', lang)}</span>
                      }
                    </td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      {p.activeVersionId && (
                        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => openView(p)} disabled={loadingView}>
                          {t('View', 'Ver', lang)}
                        </button>
                      )}
                      <Link to={`/sqf/policies/${p.id}/edit`} className="btn btn-outline-primary btn-sm me-1">{t('Edit', 'Editar', lang)}</Link>
                      {!p.activeVersionId && (
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setConfirmDeletePolicy(p)}
                          disabled={deletingId === p.id}
                          title={t('Delete this empty policy', 'Eliminar esta política vacía', lang)}
                        >
                          {deletingId === p.id ? '…' : t('Delete', 'Eliminar', lang)}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {archived.length > 0 && (
        <details className="mt-3">
          <summary className="text-muted" style={{ fontSize: 13, cursor: 'pointer' }}>{t('Archived', 'Archivados', lang)} ({archived.length})</summary>
          <ul className="mt-2 list-unstyled ps-2">
            {archived.map(p => (
              <li key={p.id} className="text-muted" style={{ fontSize: 13 }}>{p.name}</li>
            ))}
          </ul>
        </details>
      )}

      {/* ── View published policy modal ── */}
      {viewPolicy && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {viewLang === 'es' && viewPolicy.policy.nameEs ? viewPolicy.policy.nameEs : viewPolicy.policy.name}
                </h5>
                <button className="btn-close" onClick={() => setViewPolicy(null)} />
              </div>
              <div className="modal-body">
                <PolicyDocumentView
                  policy={viewPolicy.policy}
                  version={viewPolicy.version}
                  lang={lang}
                  viewLang={viewLang}
                  onViewLangChange={setViewLang}
                  allVersions={(viewPolicy.policy.Versions || [])
                    .filter(v => v.status === 'published')
                    .sort((a, b) => b.versionNumber - a.versionNumber)}
                />
              </div>
              <div className="modal-footer">
                <Link to={`/sqf/policies/${viewPolicy.policy.id}/edit`} className="btn btn-outline-primary btn-sm">
                  {t('Edit Policy', 'Editar Política', lang)}
                </Link>
                <button className="btn btn-secondary" onClick={() => setViewPolicy(null)}>{t('Close', 'Cerrar', lang)}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New Policy modal ── */}
      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('New Policy', 'Nueva Política', lang)}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Name', 'Nombre', lang)} <span className="text-danger">*</span></label>
                  <input className="form-control" value={newName} onChange={e => setNewName(e.target.value)}
                    placeholder={t('e.g. Contractor Health & Safety Agreement', 'ej. Acuerdo de Salud y Seguridad para Contratistas', lang)} autoFocus />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Category', 'Categoría', lang)}</label>
                    <select className="form-select" value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Language', 'Idioma', lang)}</label>
                    <select className="form-select" value={newDocLang} onChange={e => setNewDocLang(e.target.value)}>
                      <option value="en">🇺🇸 English</option>
                      <option value="es">🇪🇸 Español</option>
                      <option value="both">🇺🇸🇪🇸 Both</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('SQF Chapter Reference', 'Referencia de Capítulo SQF', lang)} <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({t('optional', 'opcional', lang)})</span></label>
                  <input className="form-control" value={newChapterRef} onChange={e => setNewChapterRef(e.target.value)}
                    placeholder="e.g. 2.4.2" />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Description', 'Descripción', lang)} <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({t('optional', 'opcional', lang)})</span></label>
                  <input className="form-control" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !newName.trim()}>
                  {creating ? t('Creating…', 'Creando…', lang) : t('Create & Edit', 'Crear y Editar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Generate with AI modal ── */}
      {showGenModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🤖 {t('Generate Policy with AI', 'Generar Política con IA', lang)}</h5>
                <button className="btn-close" onClick={() => setShowGenModal(false)} />
              </div>
              <div className="modal-body">
                <div className="alert alert-info py-2 mb-3" style={{ fontSize: 12 }}>
                  {t(
                    'Describe the policy you need. AI will draft the full document based on SQF standards and food safety best practices.',
                    'Describe la política que necesitas. La IA redactará el documento completo basándose en los estándares SQF y las mejores prácticas de seguridad alimentaria.',
                    lang
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('What policy do you need?', '¿Qué política necesitas?', lang)} <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows={3} value={genPrompt} onChange={e => setGenPrompt(e.target.value)}
                    placeholder={t(
                      'e.g. Pre-operation sanitation checklist policy for a bread production facility, covering equipment cleaning and sanitation verification before daily production runs.',
                      'ej. Política de lista de verificación de saneamiento pre-operativo para una instalación de producción de pan, que cubra la limpieza de equipos y la verificación del saneamiento antes de las corridas de producción diarias.',
                      lang
                    )}
                    autoFocus />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Category', 'Categoría', lang)}</label>
                    <select className="form-select" value={genCategory} onChange={e => setGenCategory(e.target.value)}>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Language', 'Idioma', lang)}</label>
                    <select className="form-select" value={genLanguage} onChange={e => setGenLanguage(e.target.value)}>
                      <option value="en">🇺🇸 English</option>
                      <option value="es">🇪🇸 Español</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('SQF Chapter Reference', 'Referencia de Capítulo SQF', lang)} <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({t('optional', 'opcional', lang)})</span></label>
                  <input className="form-control" value={genChapterRef} onChange={e => setGenChapterRef(e.target.value)} placeholder="e.g. 2.4.2" />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowGenModal(false)} disabled={generating}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || !genPrompt.trim()}>
                  {generating ? (
                    <><span className="spinner-border spinner-border-sm me-2" />{t('Generating…', 'Generando…', lang)}</>
                  ) : (
                    `🤖 ${t('Generate Policy', 'Generar Política', lang)}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Confirm delete modal ── */}
      {confirmDeletePolicy && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title text-danger">
                  {t('Delete Policy', 'Eliminar Política', lang)}
                </h5>
                <button className="btn-close" onClick={() => setConfirmDeletePolicy(null)} />
              </div>
              <div className="modal-body pt-2">
                <p className="mb-1">
                  {t('You are about to permanently delete:', 'Estás a punto de eliminar permanentemente:', lang)}
                </p>
                <p className="fw-semibold mb-3">
                  "{(lang === 'es' && confirmDeletePolicy.nameEs) ? confirmDeletePolicy.nameEs : confirmDeletePolicy.name}"
                </p>
                <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 13 }}>
                  {t('This action cannot be undone.', 'Esta acción no se puede deshacer.', lang)}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setConfirmDeletePolicy(null)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-danger" onClick={handleDeletePermanent}>
                  {t('Delete Permanently', 'Eliminar Permanentemente', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}
